import ReactCountryFlag from 'react-country-flag';
import './video-card.scss';
import { observer } from 'mobx-react-lite';
import { useState, useEffect } from 'react';
import ChannelInfo from './ChannelInfo';
import VideoStatistics from './VideoStatistics';
import LanguageStore from '../../store/LanguageStore';
import VideoListStore from '../../store/VideoListStore';
import { countries } from '../../constants/countries';
import DateService from '../../services/DateService';
import CountryGuessGame from '../CountryGuessGame/CountryGuessGame';
import GameModeSelector from '../GameModeSelector/GameModeSelector';
import GameInvite from '../GameInvite/GameInvite';
import GameService from '../../services/GameService';

const VideoCard = observer(({video, isFirst = false}) => {
    const [showAllFlags, setShowAllFlags] = useState(false);
    const [gameEnabled, setGameEnabled] = useState(false);
    const [gameMode, setGameMode] = useState(null); // 'single' | 'multi' | null
    const [gameCountry, setGameCountry] = useState(null);
    const [guessedCountries, setGuessedCountries] = useState(new Set());
    const [gameSession, setGameSession] = useState(null);
    const [inviteLink, setInviteLink] = useState(null);
    const [isMultiplayer, setIsMultiplayer] = useState(false);
    const [opponentProgress, setOpponentProgress] = useState([]);
    const [myScore, setMyScore] = useState(0);
    const [opponentScore, setOpponentScore] = useState(0);
    const [myErrors, setMyErrors] = useState(0);
    const [opponentErrors, setOpponentErrors] = useState(0);
    
    // Вычисляем текущий счет на основе угаданных стран
    const currentScore = guessedCountries.size;
    const t = LanguageStore.t;
    const {language} = LanguageStore;
    const {trackedAt} = VideoListStore;
    const sourceUrl = 'https://www.youtube.com/watch?v=' + video.videoId;

    const getCountryName = (countryCode) => {
        const country = countries.find(c => c.code === countryCode);
        return country ? country[language] || country.gb : countryCode;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        
        // Парсим дату в формате YYYY-MM-DD
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        
        const monthsRu = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 
                         'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
        const monthsGb = ['January', 'February', 'March', 'April', 'May', 'June',
                         'July', 'August', 'September', 'October', 'November', 'December'];
        
        const monthName = language === 'ru' ? monthsRu[date.getMonth()] : monthsGb[date.getMonth()];

        return language === 'ru' ? `${day} ${monthName} ${year}` : `${monthName} ${day}, ${year}`;
    };

    const isToday = () => {
        const today = DateService.getTodayFormatedDate();
        return trackedAt === today;
    };

    const getCountryTitle = (countryCode) => {
        if (!trackedAt) return '';
        
        const countryName = getCountryName(countryCode);
        
        if (isToday()) {
            return t('videoTrendingToday', countryName);
        } else {
            const formattedDate = formatDate(trackedAt);
            return t('videoTrendingDate', formattedDate, countryName);
        }
    };

    // Получаем следующую неугаданную страну
    const getNextUnguessedCountry = (currentGuessed) => {
        const allCountries = video.countriesWithPositions.map(c => c.code);
        const unguessed = allCountries.filter(code => !currentGuessed.has(code));
        return unguessed.length > 0 ? unguessed[0] : null;
    };

    // Обработчик правильного ответа (только для правильных ответов!)
    const handleCorrectAnswer = async () => {
        if (!gameCountry) return;
        
        // Проверяем, была ли страна уже угадана
        // Если страна УЖЕ в guessedCountries, значит это повторный вызов - игнорируем
        if (guessedCountries.has(gameCountry)) {
            return;
        }
        
        // Страна НЕ в guessedCountries - это правильный ответ
        // Добавляем страну в угаданные
        const newGuessed = new Set([...guessedCountries, gameCountry]);
        setGuessedCountries(newGuessed);
        
        // Обновляем счет локально сразу (оптимистичное обновление)
        if (isMultiplayer && gameSession) {
            const newScore = newGuessed.size;
            const playerId = GameService.getPlayerId();
            const isP1 = gameSession.player1Id === playerId;
            
            // Обновляем локальный счет сразу
            setMyScore(newScore);
            
            // Обновляем прогресс на сервере
            // После обновления на сервере счет соперника обновится через Mercure
            GameService.updateProgress(
                gameSession.sessionId,
                playerId,
                Array.from(newGuessed),
                myErrors
            ).catch(err => {
                console.error('[VideoCard] Error updating progress:', err);
            });
        }
        
        // Находим следующую неугаданную страну
        const nextCountry = getNextUnguessedCountry(newGuessed);
        if (nextCountry) {
            setGameCountry(nextCountry);
        } else {
            // Если все страны угаданы, закрываем окно
            setGameCountry(null);
        }
    };

    // Обработчик продолжения после ошибки (без увеличения счета)
    const handleContinue = () => {
        if (!gameCountry) return;
        
        // Просто переходим к следующей неугаданной стране без обновления прогресса
        const nextCountry = getNextUnguessedCountry(guessedCountries);
        if (nextCountry && nextCountry !== gameCountry) {
            setGameCountry(nextCountry);
        } else if (!nextCountry) {
            // Если все страны угаданы, закрываем окно
            setGameCountry(null);
        }
        // Если nextCountry === gameCountry, ничего не делаем (не должно происходить)
    };

    // Обработчик неправильного ответа
    const handleIncorrectAnswer = async () => {
        const newErrors = myErrors + 1;
        setMyErrors(newErrors);
        
        // Обновляем ошибки на сервере (только для мультиплеера)
        if (isMultiplayer && gameSession) {
            const playerId = GameService.getPlayerId();
            GameService.updateProgress(
                gameSession.sessionId,
                playerId,
                Array.from(guessedCountries),
                newErrors
            ).catch(err => {
                console.error('[VideoCard] Error updating errors:', err);
            });
        }
    };

    // Сброс игры при выключении
    const handleGameToggle = (enabled) => {
        setGameEnabled(enabled);
        if (!enabled) {
            setGuessedCountries(new Set());
            setGameCountry(null);
            setGameMode(null);
            setGameSession(null);
            setInviteLink(null);
            setIsMultiplayer(false);
            setMyErrors(0);
            setOpponentErrors(0);
        } else {
            // При включении игры показываем выбор режима
            setGameMode('select');
        }
    };

    // Обработчик выбора одиночного режима
    const handleSinglePlayer = () => {
        setGameMode('single');
        setIsMultiplayer(false);
        setMyErrors(0); // Сбрасываем счетчик ошибок при начале новой игры
        setGuessedCountries(new Set()); // Сбрасываем угаданные страны
        const firstCountry = video.countriesWithPositions[0]?.code;
        if (firstCountry) {
            setGameCountry(firstCountry);
        }
    };

    // Начать мультиплеерную игру
    const startMultiplayerGame = async () => {
        if (gameSession && gameSession.sessionId) {
            // Обновляем данные сессии с сервера для получения актуальных данных
            try {
                const result = await GameService.getSession(gameSession.sessionId);
                if (result.success && result.session) {
                    const updatedSession = result.session;
                    setGameSession(updatedSession);
                    
                    const firstCountry = video.countriesWithPositions[0]?.code;
                    if (firstCountry) {
                        setGameCountry(firstCountry);
                        // Закрываем модальное окно приглашения
                        setInviteLink(null);
                    }
                    
                    // Инициализируем счет из обновленной сессии
                    const playerId = GameService.getPlayerId();
                    const isP1 = updatedSession.player1Id === playerId;
                    
                    if (isP1) {
                        setMyScore(updatedSession.player1Score || 0);
                        setOpponentScore(updatedSession.player2Score || 0);
                        setMyErrors(updatedSession.player1Errors || 0);
                        setOpponentErrors(updatedSession.player2Errors || 0);
                        if (updatedSession.player1Progress && Array.isArray(updatedSession.player1Progress)) {
                            setGuessedCountries(new Set(updatedSession.player1Progress));
                        }
                    } else {
                        setMyScore(updatedSession.player2Score || 0);
                        setOpponentScore(updatedSession.player1Score || 0);
                        setMyErrors(updatedSession.player2Errors || 0);
                        setOpponentErrors(updatedSession.player1Errors || 0);
                        if (updatedSession.player2Progress && Array.isArray(updatedSession.player2Progress)) {
                            setGuessedCountries(new Set(updatedSession.player2Progress));
                        }
                    }
                }
            } catch (err) {
                console.error('[VideoCard] Error getting session:', err);
                console.error('[VideoCard] Error details:', {
                    message: err.message,
                    response: err.response?.data,
                    status: err.response?.status
                });
                // Fallback на старые данные
                const firstCountry = video.countriesWithPositions[0]?.code;
                if (firstCountry) {
                    setGameCountry(firstCountry);
                    setInviteLink(null);
                }
            }
        } else {
            console.error('[VideoCard] startMultiplayerGame called but gameSession is missing:', { gameSession });
        }
    };

    // Обработчик выбора мультиплеерного режима
    const handleMultiPlayer = async () => {
        setGameMode('multi');
        setIsMultiplayer(true);
        
        try {
            const countries = video.countriesWithPositions.map(c => c.code);
            const result = await GameService.createSession(video.videoId, countries);
            
            if (result.success && result.session) {
                setGameSession(result.session);
                setInviteLink(result.session.inviteLink);
            } else {
                console.error('Failed to create session:', result);
                handleSinglePlayer();
            }
        } catch (error) {
            console.error('Error creating game session:', error);
            console.error('Error details:', error.response?.data || error.message);
            // В случае ошибки переключаемся на одиночный режим
            handleSinglePlayer();
        }
    };

    // Обработчик закрытия выбора режима
    const handleCloseModeSelector = () => {
        setGameMode(null);
        setGameEnabled(false);
    };

    // Синхронизация прогресса для мультиплеерного режима
    useEffect(() => {
        if (!isMultiplayer || !gameSession || !gameSession.sessionId) {
            return;
        }

        const sessionId = gameSession.sessionId;
        const player1Id = gameSession.player1Id; // Сохраняем player1Id из актуального gameSession
        const player2Id = gameSession.player2Id; // Сохраняем player2Id из актуального gameSession
        const connectMercure = async () => {
            const mercureUrl = process.env.REACT_APP_MERCURE_URL || 'http://localhost:3001/.well-known/mercure';
            const url = new URL(mercureUrl);
            url.searchParams.append('topic', `game-session/${sessionId}`);
            
            // Получаем JWT токен для Mercure
            // Mercure принимает токен через query параметр authorization без префикса "Bearer"
            try {
                const token = await GameService.getMercureToken();
                url.searchParams.append('authorization', token);
            } catch (error) {
                console.error('[VideoCard] Failed to get Mercure token:', error);
                // Продолжаем без токена, возможно Mercure разрешит анонимный доступ
            }

            const eventSource = new EventSource(url);
            const playerId = GameService.getPlayerId();
            let pollingInterval = null;

            eventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    
                    // Обрабатываем событие подключения второго игрока
                    if (data.type === 'player_joined' && data.isFull) {
                        // Запускаем игру для основного игрока
                        startMultiplayerGame();
                        return;
                    }
                    
                    // Обрабатываем обновление прогресса
                    if (data.type === 'progress_updated') {
                        // Определяем, кто мы, используя playerId из замыкания и player1Id/player2Id из актуального gameSession
                        // ВАЖНО: player1Id и player2Id сохраняются из актуального gameSession при создании EventSource
                        // Используем функциональное обновление для доступа к актуальному gameSession
                        setGameSession(currentSession => {
                            if (!currentSession) return currentSession;
                            
                            // Используем актуальный gameSession для определения роли
                            const currentPlayer1Id = currentSession.player1Id;
                            const currentPlayer2Id = currentSession.player2Id;
                            const isCurrentPlayerP1 = currentPlayer1Id === playerId;
                            
                            // Обновляем счет в зависимости от того, кто мы
                            // ВАЖНО: для первого игрока наш счет = player1Score, счет соперника = player2Score
                            // Для второго игрока наш счет = player2Score, счет соперника = player1Score
                            if (isCurrentPlayerP1) {
                                // Мы первый игрок - наш счет это player1Score, счет соперника это player2Score
                                setMyScore(data.player1Score || 0);
                                setOpponentScore(data.player2Score || 0);
                                setMyErrors(data.player1Errors || 0);
                                setOpponentErrors(data.player2Errors || 0);
                                setOpponentProgress(data.player2Progress || []);
                            } else if (currentPlayer2Id === playerId) {
                                // Мы второй игрок - наш счет это player2Score, счет соперника это player1Score
                                setMyScore(data.player2Score || 0);
                                setOpponentScore(data.player1Score || 0);
                                setMyErrors(data.player2Errors || 0);
                                setOpponentErrors(data.player1Errors || 0);
                                setOpponentProgress(data.player1Progress || []);
                            } else {
                                console.warn('[VideoCard] Player ID does not match player1Id or player2Id:', { playerId, currentPlayer1Id, currentPlayer2Id });
                            }
                            
                            return currentSession;
                        });
                    }
                } catch (err) {
                    console.error('[VideoCard] Error parsing Mercure message:', err);
                }
            };

            eventSource.onerror = (error) => {
                console.error('[VideoCard] Mercure connection error:', error);
                console.error('[VideoCard] EventSource readyState:', eventSource.readyState);
                // Не закрываем соединение сразу - EventSource может переподключиться автоматически
                // Если readyState === 2 (CLOSED), значит соединение закрыто окончательно
                if (eventSource.readyState === EventSource.CLOSED) {
                    // Fallback на polling при окончательном закрытии
                    if (!pollingInterval) {
                        pollingInterval = setInterval(async () => {
                            try {
                                const result = await GameService.getSession(sessionId);
                                if (result.success && result.session) {
                                    const session = result.session;
                                    const isP1 = session.player1Id === playerId;
                                    
                                    if (isP1) {
                                        setMyScore(session.player1Score || 0);
                                        setOpponentScore(session.player2Score || 0);
                                        setMyErrors(session.player1Errors || 0);
                                        setOpponentErrors(session.player2Errors || 0);
                                        setOpponentProgress(session.player2Progress || []);
                                    } else {
                                        setMyScore(session.player2Score || 0);
                                        setOpponentScore(session.player1Score || 0);
                                        setMyErrors(session.player2Errors || 0);
                                        setOpponentErrors(session.player1Errors || 0);
                                        setOpponentProgress(session.player1Progress || []);
                                    }
                                }
                            } catch (err) {
                                console.error('[VideoCard] Error syncing progress:', err);
                            }
                        }, 2000);
                    }
                }
            };

            eventSource.onopen = () => {
                // Если был polling, останавливаем его
                if (pollingInterval) {
                    clearInterval(pollingInterval);
                    pollingInterval = null;
                }
            };

            return () => {
                if (pollingInterval) {
                    clearInterval(pollingInterval);
                }
                if (eventSource.readyState !== EventSource.CLOSED) {
                    eventSource.close();
                }
            };
        };

        connectMercure();

        return () => {};
    }, [isMultiplayer, gameSession?.sessionId, gameSession?.player1Id, gameSession?.player2Id]);

    return(
        <div className="video-row">
            <div className="video-row__left">
                <a href={sourceUrl} target="_blank" rel="noreferrer">
                    <img
                        src={video.imageWebUrl}
                        alt={video.title}
                        width={video.width}
                        height={video.height}
                        loading="lazy"
                        className="video-row__cover"
                    />
                </a>
                <VideoStatistics
                    videoId={video.videoId}
                    viewCount={video.viewCount}
                    likeCount={video.likeCount}
                    commentCount={video.commentCount}
                />
                <ChannelInfo
                    image={video.channelImageWebUrl}
                    title={video.channelTitle}
                    subscriberCount={video.channelSubscriberCount}
                />
            </div>
            <div className="video-row__right">
                <a href={sourceUrl} target="_blank" rel="noreferrer" className="video-row__title">
                    {video.title}
                </a>
                {/* <p className="video-row__description">
                    TODO - не приходит описание с бека
                </p> */}

                {isFirst && video.countriesWithPositions && video.countriesWithPositions.length >= 6 && (
                    <div className="video-row__game-toggle">
                        <label className="video-row__game-toggle-label">
                            <input
                                type="checkbox"
                                checked={gameEnabled}
                                onChange={(e) => handleGameToggle(e.target.checked)}
                                className="video-row__game-toggle-input"
                            />
                            <span className="video-row__game-toggle-text">{t('enableGame')}</span>
                        </label>
                    </div>
                )}

                <div className={`video-row__contries ${showAllFlags ? 'video-row__contries--expanded' : ''}`}>
                    {video.countriesWithPositions.map((contry, index) => {
                        const isGuessed = guessedCountries.has(contry.code);
                        const isGameMode = isFirst && gameEnabled;
                        const isGrayed = isGameMode && !isGuessed;
                        const isMultiplayerMode = isGameMode && gameMode === 'multi' && isMultiplayer;

                        const handleFlagClick = () => {
                            // Открываем модальное окно только при клике, не при hover
                            if (isGameMode && !isGuessed && gameMode === 'single') {
                                setGameCountry(contry.code);
                            } else if (isMultiplayerMode && !isGuessed) {
                                // В мультиплеерном режиме тоже можно кликнуть на флаг для продолжения игры
                                setGameCountry(contry.code);
                            }
                        };

                        return <div className="video-row__contry" key={index}>
                            <div
                                onClick={(isGameMode && !isGuessed && gameMode === 'single') || (isMultiplayerMode && !isGuessed) ? handleFlagClick : undefined}
                                style={(isGameMode && !isGuessed && gameMode === 'single') || (isMultiplayerMode && !isGuessed) ? { cursor: 'pointer' } : {}}
                                className={isGrayed ? 'video-row__flag-wrapper--grayed' : ''}
                            >
                                <ReactCountryFlag
                                    countryCode={contry.code}
                                    svg
                                    className={`video-row__flag ${isGuessed ? 'video-row__flag--guessed' : ''}`}
                                    style={{
                                        width: '100%',
                                        height: 'auto'
                                    }}
                                />
                            </div>

                            {isGuessed && (
                                <span className="video-row__country-name">
                                    {getCountryName(contry.code)}
                                </span>
                            )}

                            <span 
                                className="video-row__contry-value"
                                title={getCountryTitle(contry.code)}
                            >
                                {contry.value} {t('position')}
                            </span>
                        </div>
                    })}
                </div>

                {isFirst && gameMode === 'select' && (
                    <GameModeSelector
                        onSinglePlayer={handleSinglePlayer}
                        onMultiPlayer={handleMultiPlayer}
                        onClose={handleCloseModeSelector}
                    />
                )}

                {isFirst && gameMode === 'multi' && inviteLink && gameSession && (
                    <>
                        <GameInvite
                            inviteLink={inviteLink}
                            sessionId={gameSession.sessionId}
                            onClose={() => {
                                setGameMode(null);
                                setGameEnabled(false);
                            }}
                            onPlayerJoined={startMultiplayerGame}
                        />
                    </>
                )}

                {isFirst && gameMode === 'single' && gameCountry && (
                    <CountryGuessGame
                        key={gameCountry}
                        countryCode={gameCountry}
                        videoCountries={video.countriesWithPositions}
                        onClose={() => setGameCountry(null)}
                        onCorrectAnswer={handleCorrectAnswer}
                        onIncorrectAnswer={handleIncorrectAnswer}
                        onContinue={handleContinue}
                        myScore={guessedCountries.size}
                        myErrors={myErrors}
                    />
                )}

                {isFirst && gameMode === 'multi' && isMultiplayer && gameCountry && gameSession && (
                    <>
                        <CountryGuessGame
                            key={`${gameCountry}-${gameSession.sessionId}-multi`}
                            countryCode={gameCountry}
                            videoCountries={video.countriesWithPositions}
                            onClose={() => setGameCountry(null)}
                            onCorrectAnswer={handleCorrectAnswer}
                            onIncorrectAnswer={handleIncorrectAnswer}
                            onContinue={handleContinue}
                            myScore={myScore}
                            opponentScore={opponentScore}
                            myErrors={myErrors}
                            opponentErrors={opponentErrors}
                        />
                    </>
                )}
                
                {video.countriesWithPositions.length > 3 && (
                    <button 
                        className="video-row__show-more"
                        onClick={() => setShowAllFlags(!showAllFlags)}
                    >
                        {showAllFlags ? t('hide') : `${t('showAll')} (${video.countriesWithPositions.length})`}
                    </button>
                )}

            </div>
        </div>
    );
});

export default VideoCard;